import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import InventoryDetailToolbar from '../components/toolbars/InventoryToolbar';
import '../index.css';

const InventoryDetail = () => {
    const { logID } = useParams();
    const navigate = useNavigate();
    const [details, setDetails] = useState([]);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // New state for quick add form
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [brands, setBrands] = useState([]);
    const [fancifulNames, setFancifulNames] = useState([]);
    const [vineyards, setVineyards] = useState([]);
    const [modalProduct, setModalProduct] = useState({
        proVintage: '',
        proBrand: '',
        proFanciful: '',
        proVarietal: '',
        proVineyard: '',
        proWineType: '',
        proBottledDate: '',
        proBottledQuantity: '',
        proLiters: '',
        proPack: '',
        proWeight: '',
        proAlc: '',
        proVersion: ''
    });
    const [vintages, setVintages] = useState([]);
    const [varietals, setVarietals] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const generateVintages = () => {
            const currentYear = new Date().getFullYear();
            const years = ['0'];
            for (let i = currentYear; i >= currentYear - 12; i--) {
                years.push(i.toString());
            }
            setVintages(years);
        };

        const fetchInitialData = async () => {
            try {
                const [
                    varietalsResponse, 
                    vineyardsResponse,
                    brandsResponse, 
                    fancifulResponse
                ] = await Promise.all([
                    axios.get('/api/varietals'),
                    axios.get('/api/vineyards'),
                    axios.get('/api/brands'),
                    axios.get('/api/fanciful-names')
                ]);

                setVarietals(varietalsResponse.data);
                setVineyards(vineyardsResponse.data);
                setBrands(brandsResponse.data);
                setFancifulNames(fancifulResponse.data);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };

        generateVintages();
        fetchInitialData();
        fetchInventoryDetails();
    }, [logID]);

    const fetchInventoryDetails = async () => {
        try {
            const res = await axios.get(`/api/inventory-logs/${logID}`);
            if (Array.isArray(res.data)) {
                const enhancedData = res.data.map(item => ({
                    ...item,
                    difference: 0,
                    hasChanged: false,
                    proVintage: item.proVintage || '',
                    proVarietal: item.proVarietal || '',
                    proFanciful: item.proFanciful || '',
                    proVineyard: item.proVineyard || '',
                    proWineType: item.proWineType || ''
                }));
                setDetails(enhancedData);
            }
        } catch (err) {
            console.error('Error fetching inventory details:', err);
            setError('Failed to load inventory details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuantityChange = (productID, value) => {
        const numValue = parseInt(value) || 0;
        setDetails(prev => 
            prev.map(detail => {
                if (detail.productID === productID) {
                    const difference = numValue - detail.previousQuantity;
                    return {
                        ...detail,
                        currentQuantity: numValue,
                        difference,
                        hasChanged: true
                    };
                }
                return detail;
            })
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updatedDetails = details
                .filter(detail => detail.hasChanged)
                .map(({ productID, currentQuantity }) => ({
                    productID,
                    currentQuantity
                }));

            await axios.post(`/api/inventory-logs/${logID}`, {
                details: updatedDetails
            });

            setShowConfirmModal(true);
        } catch (err) {
            console.error('Error saving inventory:', err);
            setError('Failed to save inventory changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuickAddProduct = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const productData = {
                ...modalProduct,
                proBottledDate: modalProduct.proBottledDate 
                    ? new Date(modalProduct.proBottledDate).toISOString().split('T')[0] 
                    : null,
                proBottledQuantity: parseInt(modalProduct.proBottledQuantity) || 0,
                proLiters: parseFloat(modalProduct.proLiters) || 0,
                proPack: parseInt(modalProduct.proPack) || 0,
                proWeight: parseFloat(modalProduct.proWeight) || 0,
                proAlc: parseFloat(modalProduct.proAlc) || 0
            };

            const response = await axios.post('/api/products', productData);
            
            // Refresh the inventory details to include the new product
            await fetchInventoryDetails();
            
            setShowForm(false);
        } catch (error) {
            console.error('Error saving product:', error);
            setError(error.response?.data?.message || 'Error saving product');
        } finally {
            setSaving(false);
        }
    };

    const filteredDetails = details.filter(detail =>
        detail.proFanciful.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading) {
        return (
            <>
                <InventoryDetailToolbar 
                    onSave={handleSave}
                    onBack={() => navigate('/inventory')}
                    isSaving={isSaving}
                    hasChanges={details.some(d => d.hasChanged)}
                    details={filteredDetails}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                />
                <div className="container mt-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <InventoryDetailToolbar 
                onSave={handleSave}
                onBack={() => navigate('/inventory')}
                isSaving={isSaving}
                hasChanges={details.some(d => d.hasChanged)}
                details={filteredDetails}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
            />
            
            <div className="container mt-4">
                {/* Quick Add Form */}
                {showForm && (
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="mb-0">Add New Bottled Wine</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleQuickAddProduct}>
                                {error && (
                                    <div className="alert alert-danger">
                                        {error}
                                    </div>
                                )}
                                
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Vintage</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVintage}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVintage: e.target.value
                                            })}
                                        >
                                            <option value="0">Non-Vintage</option>
                                            {vintages.filter(v => v !== '0').map((vintage, index) => (
                                                <option key={index} value={vintage}>
                                                    {vintage}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Brand</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proBrand}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBrand: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Brand</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.name}>
                                                    {brand.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Version</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={modalProduct.proVersion}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVersion: e.target.value
                                            })}
                                            placeholder="e.g., v1, 45"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Fanciful Name</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proFanciful}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proFanciful: e.target.value
                                            })}
                                        >
                                            <option value="">Select Fanciful Name</option>
                                            {fancifulNames.map((name) => (
                                                <option key={name.id} value={name.name}>
                                                    {name.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Varietal</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVarietal}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVarietal: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Varietal</option>
                                            {varietals.map((varietal, index) => (
                                                <option key={index} value={varietal}>
                                                    {varietal}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Vineyard</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVineyard}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVineyard: e.target.value
                                            })}
                                        >
                                            <option value="">Select Vineyard</option>
                                            {vineyards.map((vineyard) => (
                                                <option key={vineyard.id} value={vineyard.vineyardName}>
                                                    {vineyard.vineyardName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Wine Type</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proWineType}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proWineType: e.target.value
                                            })}
                                        >
                                            <option value="">Select Wine Style</option>
                                            <option value="Red">Red</option>
                                            <option value="White">White</option>
                                            <option value="Rose">Rosé</option>
                                            <option value="Sweet">Sweet</option>
                                            <option value="Late Harvest">Dessert</option>
                                            <option value="Piquette">Piquette</option>
                                            <option value="Pet-Nat">Pet-Nat</option>
                                            <option value="Port">Port</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Bottled Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={modalProduct.proBottledDate}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBottledDate: e.target.value
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Bottled Quantity</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={modalProduct.proBottledQuantity}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBottledQuantity: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Alcohol Content (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="form-control"
                                            value={modalProduct.proAlc}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proAlc: Math.max(0, Math.min(100, Number(e.target.value)))
                                            })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Liters</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.001"
                                            className="form-control"
                                            value={modalProduct.proLiters}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proLiters: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Pack</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={modalProduct.proPack}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proPack: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Weight</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={modalProduct.proWeight}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proWeight: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            setShowForm(false);
                                            setIsEditing(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-outline-primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Add Bottled Wine'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Inventory Items</h5>
                        <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setShowForm(true)}
                        >
                            Quick Add Item
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th className="text-center">Previous Count</th>
                                        <th className="text-center">Current Count</th>
                                        <th className="text-center">Difference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDetails.map(detail => (
                                        <tr 
                                            key={detail.productID}
                                            className={detail.hasChanged ? 'table-warning' : ''}
                                        >
                                            <td>
                                                {detail.proVintage} {detail.proVarietal} {detail.proFanciful}
                                                <br />
                                                <small className="text-muted">
                                                    {detail.proWineType} | {detail.proVineyard}
                                                </small>
                                            </td>
                                            <td className="text-center">{detail.previousQuantity}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    className="form-control text-center"
                                                    value={detail.currentQuantity || ''}
                                                    onChange={(e) => handleQuantityChange(detail.productID, e.target.value)}
                                                    min="0"
                                                />
                                            </td>
                                            <td className={`text-center ${detail.difference < 0 ? 'text-danger' : detail.difference > 0 ? 'text-success' : ''}`}>
                                                {detail.difference > 0 ? '+' : ''}{detail.difference}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Confirmation Modal */}
                {showConfirmModal && (
                    <>
                        <div className="modal-backdrop fade show"></div>
                        <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Inventory Updated</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => navigate('/inventory')}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <p>Inventory has been successfully updated.</p>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => navigate('/inventory')}
                                        >
                                            Return to Inventory List
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default InventoryDetail;